import moment from 'moment';

import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface CreateDataCaptureAssistantChatResponse {
  chatId: string;
}

/**
 * Create a new chat object on the backend. This should be run on first meaningful
 * user action (e.g. sending a message).
 */
export function createDataCaptureAssistantChat(): Promise<CreateDataCaptureAssistantChatResponse> {
  return Net.postJSONPromise({
    url: '/1/api/llm/data-capture-assistant/chats',
  });
}

/**
 * Run the data capture assistant for the given chat, processing the given message
 * as a new user message.
 */
export async function runDataCaptureAssistantWithoutPolling(
  chatSessionId: string,
  message: string,
  fileUploads: Array<ChatFileUpload>
): Promise<{status_url: string}> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:run`,
    data: {message, fileUploads, currentDate: getCurrentDate()},
  });
}

/**
 * Run the data capture assistant for the given chat, processing the given message
 * as a new user message.
 */
export async function runDataCaptureAssistant(
  chatSessionId: string,
  message: string,
  fileUploads: Array<ChatFileUpload>
): Promise<void> {
  await TaskRunner.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:run`,
    data: {message, fileUploads, currentDate: getCurrentDate()},
  });
}

function getCurrentDate(): string {
  return moment().format('YYYY-MM-DD');
}

interface StartWebRTCRealtimeVoiceSessionResponse {
  sdpAnswer: string;
}

/**
 * For a given Compose chat session, start a realtime session with the OpenAI API.
 */
export async function startWebRTCRealtimeVoiceSession(
  chatSessionId: string,
  sdpOffer: string
): Promise<StartWebRTCRealtimeVoiceSessionResponse> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:start-webrtc-realtime-voice-session`,
    data: {sdpOffer},
  });
}

export interface VoiceToolInvocationItem {
  name: string;
  callId: string;
  args: string;
}

export interface VoiceToolInvocationResponse {
  output: string;
}

/**
 * In the context of a realtime voice session, execute a tool invoked by the LLM.
 * Note that the tool name and args have not been validated at this point and must be
 * validated by the server.
 */
export async function invokeVoiceTool(
  chatSessionId: string,
  item: VoiceToolInvocationItem
): Promise<VoiceToolInvocationResponse> {
  return await Net.postJSONPromise({
    url: `/1/api/llm/data-capture-assistant/chats/${chatSessionId}:invoke-voice-tool`,
    data: {item},
  });
}
